import { AutoComplete, Button, Col, Collapse, Create, DatePicker, Divider, Form, Icons, Input, InputNumber, Radio, Result, Row, Select, Spin, Tag, Tooltip, Typography, message, useForm, useSelect } from "@pankod/refine-antd";
import { useCreateMany, useGetIdentity, useList, useNavigation, useOne, useUpdateMany } from "@pankod/refine-core";
import { StateButtons } from "components/common";
import { ShowUserCard } from "components/profile/showUserCard";
import { CancelButton } from "components/utils/cancelButton";
import dayjs, { Dayjs } from "dayjs";
import { IBaseProps, ICountry, IListData, ILocations, ILookup, IStateTransitionComments, IUser } from "interfaces";
import { IPosition, IResourcingRequest, IResourcingRequestConfigSet, IResourcingRequestMetadata } from "interfaces/resourcing";
import { arrayExtensions, stringExtensions } from "lib-common";
import { useEffect, useState } from "react";
import { DATAPROVIDER_CONFIGSET, DATAPROVIDER_CREATE, DATAPROVIDER_DISTINCT, DATAPROVIDER_LOOKUP, DATAPROVIDER_READ, DATAPROVIDER_UPDATE, RESOURCE_PATH, SELECTION_LISTS, STALE_DURATION, arrayJoinString, buildAttachmentUrl, doesExist, generateGroupFetchID, getGUID, handleSelectChange, showDash, validateRGSIDs } from "scripts/site";

export const RequestForm: React.FC<{ dataProvider: string }> = ({ dataProvider }) => {
    const { data: userData } = useGetIdentity();
    const userObj = userData as IUser;
    const { list } = useNavigation();
    const { Panel } = Collapse;
    const { mutate: createPositions } = useCreateMany<IPosition>();
    const { mutate: updatePositions } = useUpdateMany<IPosition>();
    const { Title, Text } = Typography;

    const { form, formProps, queryResult, onFinish, redirect, formLoading } = useForm<IResourcingRequest>({
        dataProviderName: dataProvider,
        redirect: "show",
        mutationMode: "pessimistic"
    });

    const { data: metadata, isLoading: metadataLoading } = useOne<IResourcingRequestMetadata>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.METADATA,
        id: RESOURCE_PATH.RESOURCINGREQUEST,
        queryOptions: {
            enabled: true,
            staleTime: STALE_DURATION
        }
    });

    const { selectProps: countryProps, queryResult: countryQuery } = useSelect<ICountry>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.COUNTRY,
        optionLabel: "name",
        optionValue: "id",
        sort: [
            {
                field: "name",
                order: "asc"
            }
        ],
        fetchSize: 100
    });

    const { queryResult: locationsData } = useSelect<ILocations>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LOCATION,
        sort: [
            {
                field: "name",
                order: "asc"
            },
        ],
        fetchSize: 100
    });

    const { selectProps: userProps } = useSelect<ILookup>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.PEOPLE,
        optionLabel: "nameEmail",
        optionValue: "id",
        sort: [
            {
                field: "lastAccessedAt",
                order: "desc"
            },
        ],
        filters: [{
            field: "stateManager.state",
            operator: "eq",
            value: "active"
        }],
        fetchSize: 50
    });

    const { data: skillsList } = useOne<IListData>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LIST,
        id: SELECTION_LISTS.TECHNICAL_EXPERTISE
    });

    const { data: rolesList } = useOne<IListData>({
        dataProviderName: DATAPROVIDER_LOOKUP,
        resource: RESOURCE_PATH.LIST,
        id: SELECTION_LISTS.RESOURCING_ROLES
    });
    const [billingStartDate, setBillingStartDate] = useState<Dayjs | null>();

    const metaConfig = metadata?.data.config;
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const record = queryResult?.data?.data;

    if (record?.resourceRequiredDate && typeof record?.resourceRequiredDate === "string") {
        record.resourceRequiredDate = dayjs(record.resourceRequiredDate);
    }

    if (record?.billingStartDate && typeof record?.billingStartDate === "string") {
        record.billingStartDate = dayjs(record.billingStartDate);
    }

    if (record?.requestReceivedDate && typeof record?.requestReceivedDate === "string") {
        record.requestReceivedDate = dayjs(record.requestReceivedDate);
    }

    if (isInitialLoad && !formLoading && record) {
        setIsInitialLoad(false);
        if (record?.billingStartDate) {
            setBillingStartDate(dayjs(record.billingStartDate));
        }
    }


    const locations = locationsData?.data?.data;
    const countries = countryQuery?.data?.data ?? [];
    //const [canCreate, switchCanCreate] = useState(false);
    //const [canUpdate, switchCanUpdate] = useState(false);
    const [positionType, setPositionType] = useState("");
    const [billable, setBillable] = useState("");
    //const [resourcingAdmin, switchResourcingAdmin] = useState(false);
    const [requestState, switchRequestState] = useState("draft");
    const [activePanel, setActivePanel] = useState<string[]>(['basic']);
    const [posCount, setPositionCount] = useState<number>(1);
    const threeColumnLayout = { xs: { span: 24 }, sm: { span: 24 }, md: { span: 8 }, lg: { span: 8 }, xl: { span: 8 }, xxl: { span: 8 } };
    const twoColumnLaytout = { xs: { span: 24 }, sm: { span: 24 }, md: { span: 16 }, lg: { span: 16 }, xl: { span: 16 }, xxl: { span: 16 } };
    const rowGutter = { xs: 8, sm: 16, md: 24, lg: 32 };

    const isOnsiteDpRequired = (countryId?: string) => {
        const selected = countries.find((country) => country.id === countryId);
        console.log("Selected Country: ", selected);
        return selected?.resourcing?.requireOnsiteDp ?? false;
    };

    const { data: configSet, isLoading: configSetLoading } = useOne<IResourcingRequestConfigSet>({
        dataProviderName: DATAPROVIDER_CONFIGSET,
        resource: RESOURCE_PATH.RESOURCINGREQUEST,
        id: record?.id ?? "",
        queryOptions: {
            enabled: !!record?.id
        }
    });

    // Set the rules for the request based on the config set. If config set is not found, then use the metadata config as default.
    const requestRules = configSet?.data?.values ?? metaConfig?.defaultConfigSet;

    useEffect(() => {
        if (record?.stateManager) {
            switchRequestState(record?.stateManager.state);
        }
    }, [record?.stateManager]);

    useEffect(() => {
        if (record?.positionType) {
            setPositionType(record?.positionType);
        }
    }, [record?.positionType]);

    // useEffect(() => {
    //     if (record?.billingStartDate) {
    //         record.billingStartDate = dayjs(record.billingStartDate);
    //         setBillingStartDate(record?.billingStartDate);
    //     }
    // }, [record, record?.billingStartDate]);

    useEffect(() => {
        if (record?.billable) {
            setBillable(record?.billable);
        }
    }, [record?.billable]);

    useEffect(() => {
        if (record?.positionCount) {
            setPositionCount(+record.positionCount)
        }
    }, [record?.positionCount]);

    useEffect(() => {
        form.setFieldsValue({ 'positionTypeName': metaConfig?.positionType.find(m => m.id === positionType)?.name });
    }, [form, metaConfig?.positionType, positionType])


    const [validateForm, setValidateForm] = useState<boolean>(true);
    const [disableForm, setDisableForm] = useState<boolean>(false);
    const isPartialEditable = requestRules?.partialEditableStates?.includes(requestState);

    const today = dayjs().endOf('day');
    const sixMonth = dayjs().add(6, 'months').endOf('day');

    const { data: onsiteDPProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryPartnerOnsiteGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryPartnerOnsiteGroup
        }
    });

    const { data: offshoreDPProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryPartnerOffshoreGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryPartnerOffshoreGroup
        }
    });

    const { data: deliveryHeadProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.deliveryHeadGroup),
        queryOptions: {
            enabled: !!metaConfig?.deliveryHeadGroup
        }
    });

    const { data: salesExecutiveProps } = useOne<ILookup[]>({
        dataProviderName: DATAPROVIDER_READ,
        resource: RESOURCE_PATH.GROUPS,
        id: generateGroupFetchID(metaConfig?.salesExecutiveGroup),
        queryOptions: {
            enabled: !!metaConfig?.salesExecutiveGroup
        }
    });

    const submitWithComments = (values: IStateTransitionComments, stateAction: string) => {
        if (!form.getFieldValue('id')) { form.setFieldsValue({ 'id': getGUID() }) }
        if (!form.getFieldValue('requestor')) { form.setFieldsValue({ 'requestor': userObj?.id }) }

        // Add the State transition for Form Submission in case of submit
        form.setFieldsValue({
            'stateAction': stateAction,
            'requestReceivedDate': dayjs().toISOString(),
            'comments': values.comments
        })
        form.submit();
    }

    const setFormPreValidationRules = (stateAction: string) => {
        setValidateForm(!stringExtensions.stringEquals(stateAction, "revert"));
        setDisableForm(true);
    }

    const onFailureFormValidation = () => {
        setActivePanel(["basic", "aboutteam", "skills", "staffing"]);
        setDisableForm(false);
    }

    const handleFormSave = () => {
        setDisableForm(true);
        setValidateForm(!(requestState === "draft"));
        form.validateFields().then(async () => {
            let initialGUID = undefined;
            if (!form.getFieldValue('id')) {
                initialGUID = getGUID();
                form.setFieldsValue({ 'id': initialGUID });
            }
            if (!form.getFieldValue('requestor')) {
                form.setFieldsValue({ 'requestor': userObj?.id })
            }

            if (initialGUID) {
                const response = await onFinish(form.getFieldsValue());
                redirect("edit", response?.data?.id || form.getFieldValue('id'));
            }
            else {
                // Update data to all Positions 
                form.submit();
                if (record && arrayExtensions.validateArray(record?.positions ?? [])) {

                    let mergedRequest = { ...form.getFieldsValue() };
                    let fieldsNotToCopy = metaConfig?.skipFieldsCopyToPosition || [];
                    let updatedPos: { [key: string]: any } = {};
                    for (const [key, value] of Object.entries(mergedRequest)) {
                        if (!fieldsNotToCopy.includes(key)) {
                            updatedPos[key] = value;
                        }
                    }
                    updatePositions(
                        {
                            resource: RESOURCE_PATH.POSITION,
                            dataProviderName: DATAPROVIDER_UPDATE,
                            values: updatedPos,
                            ids: record.positions.map((pos) => pos.id)
                        },
                        {
                            onError: () => {
                                message.error(`Unable to update ${record.positions.length + 1} position(s) for request: ${record.id}`);
                                setDisableForm(true);
                            }
                        }
                    );
                }
            }
        })
            .catch((errorInfo) => {
                setActivePanel(["basic", "aboutteam", "skills", "staffing"]);
                setDisableForm(false);
            });
    }

    const publishWithComments = (values: IStateTransitionComments, stateAction: string) => {
        // Two state actions possible while entry is in Submitted state
        // First is Publish
        if (stateAction === "publish") {
            let reqId = form.getFieldValue('requestID');
            let rgdIdValues = form.getFieldValue('rgsIds');

            if (record && reqId && arrayExtensions.validateArray(rgdIdValues)) {
                let positions: any[] = [];

                //Disable the Form
                setDisableForm(true);

                // Create Positions after Form Validation
                message.info(`Creating ${posCount} position(s) for request: ${record.id}`);
                let epochTime = new Date().getTime();

                for (let index = 0; index < posCount; index++) {
                    // Formulate the base properties for the Position
                    let dem: { [key: string]: any } = {
                        id: `${reqId}-${epochTime}-${String(index + 1).padStart(3, "0")}`,
                        resourcingRequestID: record.id || form.getFieldValue('id'),
                        rgsId: +rgdIdValues[index] || 0,
                        empIDSelectedCandidate: "",
                        nameSelectedCandidate: "",
                        backgroundScreeningID: "",
                        closureChannel: null,
                        notes: "",
                        closureDate: "",
                        mappedDate: ""
                    }

                    // Copy the form values and merge it with record
                    let mergedRequest = { ...record, ...form.getFieldsValue() };
                    let fieldsNotToCopy = metaConfig?.skipFieldsCopyToPosition || [];
                    // Copy the values from resourcingrequest down to position
                    for (const [key, value] of Object.entries(mergedRequest)) {
                        if (!fieldsNotToCopy.includes(key)) {
                            dem[key] = value;
                        }
                    }

                    // Position count will always be 1 in all positions
                    dem["positionCount"] = 1;

                    positions.push(dem);
                }

                createPositions(
                    {
                        resource: RESOURCE_PATH.POSITION,
                        dataProviderName: DATAPROVIDER_CREATE,
                        values: positions
                    },
                    {
                        onError: () => {
                            message.error(`Unable to create ${posCount} position(s) for request: ${record.id}`);
                            setDisableForm(true);
                        },
                        onSuccess: () => {
                            // Prepeare the form for State Change
                            form.setFieldsValue({
                                'stateAction': stateAction,
                                'positions': positions.map((pos) => { return { "id": pos.id, "currentState": "open", "rgsId": pos.rgsId } }),
                                'comments': values.comments
                            })
                            // Submit the form
                            form.submit();
                        }
                    }
                )
            } else {
                // Unable to read the reqId
                message.error(`Request ID/RGS IDs is/are not found, please provide Request ID/RGS Ids and then try to publish. Please note as per business rule, these fields are mandatory to create position(s).`);
                setDisableForm(false);
            }
        }
        // Second is "Revert Back"
        else if (stateAction === "revert") {
            // Prepeare the form for State Change
            form.setFieldsValue({
                'stateAction': stateAction,
                'comments': values.comments
            })
            // Submit the form
            form.submit();
        }
    }

    const changeActivePanel = (key: string | string[]) => {
        if (typeof key === "string") {
            key = [key];
        }
        setActivePanel(key);
    };

    const metaFields = metadata?.data.fields;
    const crmGuidanceUrl = buildAttachmentUrl(RESOURCE_PATH.METADATA, RESOURCE_PATH.RESOURCINGREQUEST, metaFields?.find(f => f.name === "crmGuidance")?.attachment);

    useEffect(() => {
        if (!formLoading && positionType !== "" && positionType !== "Replacement") {
            form.setFieldsValue({
                replacementForEmployeeName: "",
                replacementForEmployeeId: "",
                replacementReason: ""
            });
        }
    }, [form, formLoading, positionType]);

    useEffect(() => {
        if (!formLoading && billable === "Non-Billable") {
            setBillingStartDate(null);
            form.setFieldsValue({
                billingType: "",
                //billingStartDate: "",
                billRate: ""
            });
        }
    }, [form, billable, formLoading]);

    useEffect(() => {
        form.setFieldsValue({
            billingStartDate: billingStartDate,
        });
    }, [form, billingStartDate]);

    // Debounce function to delay execution of fetchProjectNames function
    const debounce = (func: (...args: any[]) => void, delay: number) => {
        let timeoutId: NodeJS.Timeout | undefined;
        return (...args: any[]) => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };

    const [projectNameSearchText, setProjectNameSearchText] = useState<string>();
    const { refetch: fetchProjectNames, data: projectNameResult } = useList({
        dataProviderName: DATAPROVIDER_DISTINCT,
        resource: RESOURCE_PATH.RESOURCINGREQUEST,
        config: {
            filters: [{
                field: "_fields",
                operator: "eq",
                value: "projectName"
            },
            {
                field: "projectName",
                operator: "contains",
                value: projectNameSearchText
            }],
            sort: [{
                field: "updatedAt",
                order: "desc"
            }]
        },
        queryOptions: {
            enabled: !!projectNameSearchText
        }
    });

    const [titleText, setTitleText] = useState<string>();
    const { refetch: fetchTitle, data: titleResult } = useList({
        dataProviderName: DATAPROVIDER_DISTINCT,
        resource: RESOURCE_PATH.RESOURCINGREQUEST,
        config: {
            filters: [{
                field: "_fields",
                operator: "eq",
                value: "title"
            },
            {
                field: "title",
                operator: "contains",
                value: titleText
            }],
            sort: [{
                field: "updatedAt",
                order: "desc"
            }]
        },
        queryOptions: {
            enabled: !!titleText
        }
    });

    const projectNameData = projectNameResult?.data?.map((item: any) => ({ value: item.projectName })) ?? [];
    const titleData = titleResult?.data?.map((item: any) => ({ value: item.title })) ?? [];

    const handleProjectNameSearch = debounce(async (searchText: string) => {
        if (searchText?.length > 2) {
            setProjectNameSearchText(searchText);
            await fetchProjectNames();
        }
    }, 500);

    const handleTitleSearch = debounce(async (searchText: string) => {
        if (searchText?.length > 2) {
            setTitleText(searchText);
            await fetchTitle();
        }
    }, 500);

    return (
        (metadataLoading || (record?.id && configSetLoading))
            ? <Result
                status="info"
                title="Checking permission !!!"
                subTitle="Kindly wait for few moments while we verify your permission and load this request for you."
            />
            : requestRules?.editableStates?.includes(requestState)
                ? <Spin spinning={disableForm || metadataLoading || locationsData.isLoading}>
                    <div className="engagement">
                        <Create
                            headerProps={{
                                title: record?.projectName ?? "Resourcing Request",
                                tags: record && <Tag color="blue" >{stringExtensions.capitalize(requestState)}</Tag>,
                            }}
                            footerButtons={() => (
                                <>
                                    {record &&
                                        <StateButtons {...record as IBaseProps}
                                            resourceToAct={RESOURCE_PATH.RESOURCINGREQUEST}
                                            resourceToActId={record?.id || "0"}
                                            resourceToNavigate={RESOURCE_PATH.RESOURCINGREQUEST}
                                            hideActions={["reassign", "Auto Processed"]}
                                            skipAssignedToCheck={true}
                                            form={form}
                                            setFormPreValidationRules={setFormPreValidationRules}
                                            onFailureFormValidate={onFailureFormValidation}
                                            customMutate={requestState === "submitted" ? publishWithComments : submitWithComments}
                                        />
                                    }
                                    {requestState === "draft" && record?.id === undefined &&
                                        <Button
                                            type="primary"
                                            disabled={true}
                                            icon={<Icons.FileDoneOutlined />}
                                        >
                                            Submit
                                        </Button>
                                    }
                                    <Button
                                        type="primary"
                                        style={{ backgroundColor: "green" }}
                                        onClick={() => handleFormSave()}
                                        icon={<Icons.SaveOutlined />}
                                    >
                                        Save
                                    </Button>
                                    <CancelButton />
                                </>
                            )}
                        >
                            <Form {...formProps} layout="vertical" autoComplete="off" scrollToFirstError={true}>
                                <Collapse activeKey={activePanel} expandIconPosition="end" onChange={changeActivePanel}>
                                    <Panel header={<b>Basic Details</b>} key="basic">
                                        <Form.Item
                                            label="Request ID"
                                            name="id"
                                            hidden={true}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="stateAction"
                                            hidden={true}
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            name="comments"
                                            hidden={true}
                                        >
                                            <Input />
                                        </Form.Item>
                                        {requestState === "draft" &&
                                            <>
                                                <Form.Item
                                                    name="requestReceivedDate"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    label="Requestor"
                                                    name="requestor"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </>
                                        }
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Project Name"
                                                    name="projectName"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            max: 100
                                                        },
                                                    ]}
                                                >
                                                    <AutoComplete
                                                        options={projectNameData}
                                                        placeholder="Start typing to get suggestions"
                                                        onSearch={handleProjectNameSearch}
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Role Name"
                                                    name="name"
                                                    tooltip="Select role from available list"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                        }
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select role"
                                                        showSearch
                                                    >
                                                        {rolesList?.data.data.map((m, i) => (
                                                            <Select.Option value={m} key={i}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Title"
                                                    name="title"
                                                    tooltip="Enter a title for this role if different from 'Role Name'. Default to 'Role Name' if not different."
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            max: 100
                                                        },
                                                    ]}
                                                >
                                                    <AutoComplete
                                                        options={titleData}
                                                        placeholder="Start typing to get suggestions"
                                                        onSearch={handleTitleSearch}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Position Type</Title>
                                                        <Text copyable={doesExist(record?.positionTypeName)}>{showDash(record?.positionTypeName)}</Text>
                                                    </>
                                                    : <>
                                                        <Form.Item
                                                            label="Position Type"
                                                            name="positionType"
                                                            tooltip={<>
                                                                Select the appropriate position type.
                                                                <br />
                                                                Select "New" only if you want to Hire a new resource.
                                                                <br />
                                                                Select "Replacement" for replacement of an existing resource.
                                                            </>}
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    whitespace: true
                                                                }
                                                            ]}
                                                        >
                                                            <Select
                                                                placeholder="Select Position Type"
                                                                onChange={(value) => {
                                                                    setPositionType(value);
                                                                    if (value === "Investment") {
                                                                        form.setFieldsValue({ 'billable': "Non-Billable" });
                                                                        setBillingStartDate(null);
                                                                    } else {
                                                                        form.resetFields(['billable']);
                                                                    }

                                                                    if (value === "Replacement") {
                                                                        form.setFieldsValue({ 'positionCount': 1 });
                                                                        setPositionCount(1);
                                                                    } else {
                                                                        form.resetFields(['positionCount']);
                                                                    }

                                                                    if (value === "New-Committed" || value === "Replacement") {
                                                                        form.setFieldsValue({ 'confidence': "Confirmed" });
                                                                    } else {
                                                                        form.resetFields(['confidence']);
                                                                    }
                                                                }}
                                                            >
                                                                {(metaConfig?.positionType ?? []).map((m, i) => (
                                                                    <Select.Option value={m.id} key={i}>{m.name}</Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                        <Form.Item
                                                            label="Position Type Name"
                                                            name="positionTypeName"
                                                            hidden={true}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="WON"
                                                    name="projectWON"
                                                    tooltip="WON should be 7 or 8 digits long numeric value starting with 1 or 2"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "integer",
                                                            message: "Project WON should be 7 or 8 digits long numeric value starting with 1 or 2",
                                                            validator: async (_, value) => {
                                                                if (validateForm) {
                                                                    let strValue = value.toString();
                                                                    if (strValue && strValue.length > 6 && strValue.length < 9 && (strValue.startsWith("1") || strValue.startsWith("2"))) {
                                                                        Promise.resolve();
                                                                    }
                                                                    else {
                                                                        throw new Error("WON does not match the criteria.")
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={0} precision={0} maxLength={8} placeholder="WON" style={{ width: 200 }} controls={false} />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Number of positions {(positionType === "Replacement") &&
                                                            <Tooltip title={"Replacement requests always have 1 position"}> <Icons.QuestionCircleOutlined /></Tooltip>}
                                                        </Title>
                                                        <Text copyable={doesExist(record?.positionCount)}>{showDash(record?.positionCount)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Number of positions"
                                                        name="positionCount"
                                                        tooltip="Specify the number of resources required with the skills specified in this request. For different skills/location, please create a new request."
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                type: "integer"
                                                            }
                                                        ]}
                                                    >
                                                        <InputNumber min={1} max={15} precision={0} disabled={positionType === "Replacement"} placeholder="Position count" style={{ width: 200 }}
                                                            onChange={(value) => {
                                                                setPositionCount(value || 1);
                                                            }} />
                                                    </Form.Item>
                                                }
                                            </Col>
                                        </Row>
                                        {positionType === "Replacement" ?
                                            <>
                                                <Divider orientation="left">Additional details for Replacement position</Divider>
                                                <Row gutter={rowGutter}>
                                                    <Col className="gutter-row" {...threeColumnLayout}>
                                                        <Form.Item
                                                            label="Replacement for Employee"
                                                            name="replacementForEmployeeName"
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    whitespace: true
                                                                }
                                                            ]}
                                                        >
                                                            <Input placeholder="Provide name of employee being replaced" />
                                                        </Form.Item>

                                                    </Col>
                                                    <Col className="gutter-row" {...threeColumnLayout}>
                                                        <Form.Item
                                                            label="Replacement for Employee Id"
                                                            name="replacementForEmployeeId"
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    type: "number",
                                                                    whitespace: true
                                                                }
                                                            ]}
                                                        >
                                                            <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} placeholder="Provide employee Id of employee being replaced" controls={false} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col className="gutter-row" {...threeColumnLayout}>
                                                        <Form.Item
                                                            label="Replacement reason"
                                                            name="replacementReason"
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    whitespace: true
                                                                }
                                                            ]}
                                                        >
                                                            <Select placeholder="Select reason for replacement" showSearch>
                                                                {metaConfig?.replacementReason.sort().map((m, i) => (
                                                                    <Select.Option value={m} key={`rr-${i}`}>{m}</Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Divider />
                                            </>
                                            : <>
                                                <Form.Item
                                                    name="replacementForEmployeeName"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="replacementForEmployeeId"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                                <Form.Item
                                                    name="replacementReason"
                                                    hidden={true}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </>
                                        }
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Billable/Non-Billable{positionType === "Investment" &&
                                                            <Tooltip title={"Investment requests are always non-billable"}> <Icons.QuestionCircleOutlined /></Tooltip>}
                                                        </Title>
                                                        <Text copyable={doesExist(record?.billable)}>{showDash(record?.billable)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Billable/Non Billable"
                                                        name="billable"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Radio.Group
                                                            disabled={positionType === "Investment"}
                                                            optionType="button"
                                                            buttonStyle="solid"
                                                            name="billable"
                                                            onChange={(value) => {
                                                                setBillable(value.target.value);
                                                            }}
                                                            options={(["Billable", "Non-Billable"]).map(a => a)}
                                                        />
                                                    </Form.Item>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Confidence {(positionType === "New-Committed" || positionType === "Replacement") &&
                                                            <Tooltip title={"New (Signed SOW/CR/Amendment) and Replacement requests are always confirmed"}> <Icons.QuestionCircleOutlined /></Tooltip>}
                                                        </Title>
                                                        <Text copyable={doesExist(record?.confidence)}>{showDash(record?.confidence)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Confidence"
                                                        name="confidence"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Radio.Group
                                                            optionType="button"
                                                            disabled={positionType === "New-Committed" || positionType === "Replacement"}
                                                            buttonStyle="solid"
                                                            name="confidence"
                                                            options={(["Confirmed", "Tentative"]).map(a => a)}
                                                        />
                                                    </Form.Item>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Resource Required Date</Title>
                                                        <Text copyable={doesExist(record?.resourceRequiredDate)}>{dayjs(record?.resourceRequiredDate).format("LL")}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Resource Required Date"
                                                        name="resourceRequiredDate"
                                                        tooltip="Please select a date for when the resource needs to be available for this role."
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true,
                                                                type: "date",
                                                                validator: async (_, value) => {
                                                                    if (validateForm || value) {
                                                                        if (value > today && value < (billingStartDate ?? sixMonth)) {
                                                                            return Promise.resolve();
                                                                        }
                                                                        else {
                                                                            throw new Error("Resource Required Date should be a future date and before billing start date")
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        ]}
                                                    >
                                                        <DatePicker picker="date"
                                                            style={{ width: 280 }}
                                                            placeholder="YYYY-MM-DD"
                                                            format={"YYYY-MM-DD"}
                                                            disabledDate={(selDate) => selDate && (selDate < today || selDate > (billingStartDate ?? sixMonth))}
                                                            allowClear={false}
                                                        />
                                                    </Form.Item>
                                                }
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter} style={{ marginTop: 15 }}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="CRM Id for RGS"
                                                    name="crmOpportunityId"
                                                    tooltip={<>
                                                        CRM Id for this request. Staffing should have access to the CRM Id.
                                                        <br />
                                                        {crmGuidanceUrl &&
                                                            <div>
                                                                <a href={crmGuidanceUrl} rel="noreferrer" target={"_blank"}>View guidance</a>
                                                            </div>
                                                        }
                                                    </>
                                                    }
                                                    dependencies={["positionType"]}
                                                    rules={[
                                                        {
                                                            required: (positionType !== "Replacement" && validateForm),
                                                            message: "CRM Id for RGS is required.",
                                                        },
                                                        {
                                                            whitespace: true,
                                                            type: "integer",
                                                            message: "CRM Id for RGS should be 7 digits long numeric value starting with 1 or 2",
                                                            validator: async (_, value) => {
                                                                if (value || (positionType !== "Replacement" && validateForm)) {
                                                                    let strValue = value?.toString();
                                                                    if (strValue && strValue.length === 7 && (strValue.startsWith("1") || strValue.startsWith("2"))) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    else {
                                                                        return Promise.reject(new Error("CRM Id for RGS does not match the criteria."));
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={1} precision={0} maxLength={7} placeholder="CRM Id for RGS" style={{ width: "100%" }} controls={false} />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Actual CRM Id"
                                                    name="actualCrmId"
                                                    tooltip={<>
                                                        Actual CRM Id for closing the positions. Staffing should have access to the CRM Id.
                                                        <br />
                                                        {crmGuidanceUrl &&
                                                            <div>
                                                                <a href={crmGuidanceUrl} rel="noreferrer" target={"_blank"}>View guidance</a>
                                                            </div>
                                                        }
                                                    </>
                                                    }
                                                    dependencies={["positionType"]}
                                                    rules={[
                                                        {
                                                            required: (positionType !== "Replacement" && validateForm),
                                                            message: "Actual CRM Id is required.",
                                                        },
                                                        {
                                                            whitespace: true,
                                                            type: "integer",
                                                            message: "Actual CRM Id should be 7 digits long numeric value starting with 1 or 2",
                                                            validator: async (_, value) => {
                                                                if (value || (positionType !== "Replacement" && validateForm)) {
                                                                    let strValue = value?.toString();
                                                                    if (strValue && strValue.length === 7 && (strValue.startsWith("1") || strValue.startsWith("2"))) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    else {
                                                                        return Promise.reject(new Error("Actual CRM Id does not match the criteria."));
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={1} precision={0} maxLength={7} placeholder="Actual CRM Id" style={{ width: "100%" }} controls={false} />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Form.Item noStyle
                                            shouldUpdate={(prevValues, currentValues) => prevValues.billable !== currentValues.billable}                                        >
                                            {({ getFieldValue }) =>
                                                getFieldValue('billable') === "Billable" ? (
                                                    <>
                                                        <Divider orientation="left">Additional details for billable resources</Divider>
                                                        <Row gutter={rowGutter}>
                                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                                {isPartialEditable
                                                                    ? <>
                                                                        <Title level={5}>Billing Type</Title>
                                                                        <Text copyable={doesExist(record?.billingType)}>{showDash(record?.billingType)}</Text>
                                                                    </>
                                                                    : <Form.Item
                                                                        label="Billing Type"
                                                                        name="billingType"
                                                                        rules={[
                                                                            {
                                                                                required: validateForm,
                                                                                whitespace: true
                                                                            }
                                                                        ]}
                                                                    >
                                                                        <Radio.Group
                                                                            optionType="button"
                                                                            buttonStyle="solid"
                                                                            name="billingType"
                                                                            options={(metaConfig?.billingType ?? []).map(a => a)}
                                                                        />
                                                                    </Form.Item>
                                                                }
                                                            </Col>
                                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                                {isPartialEditable && doesExist(record?.billingStartDate)
                                                                    ? <>
                                                                        <Title level={5}>Billing Start Date</Title>
                                                                        <Text copyable={doesExist(record?.billingStartDate)}>{dayjs(record?.billingStartDate).format("LL")}</Text>
                                                                    </>
                                                                    : <Form.Item
                                                                        label="Billing Start Date"
                                                                        name="billingStartDate"
                                                                        tooltip="Billing start date should be a future date and within 6 months from today"
                                                                        rules={[
                                                                            {
                                                                                required: validateForm,
                                                                                whitespace: true,
                                                                                type: "date",
                                                                                validator: async (_, value) => {
                                                                                    if (validateForm || value) {
                                                                                        if (value > today && value < sixMonth) {
                                                                                            return Promise.resolve();
                                                                                        }
                                                                                        else {
                                                                                            throw new Error("Billing start date should be a future date and within 6 months from today")
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                        ]}
                                                                    >
                                                                        <DatePicker picker="date"
                                                                            style={{ width: 280 }}
                                                                            placeholder="YYYY-MM-DD"
                                                                            format={"YYYY-MM-DD"}
                                                                            disabledDate={(selDate) => selDate && (selDate < today || selDate > sixMonth)}
                                                                            onChange={(date) => {
                                                                                setBillingStartDate(date);
                                                                                form.validateFields(['resourceRequiredDate']);
                                                                            }}
                                                                            allowClear={false}
                                                                        />
                                                                    </Form.Item>}
                                                            </Col>
                                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                                {isPartialEditable
                                                                    ? <>
                                                                        <Title level={5}>Bill Rate (USD per Hour)</Title>
                                                                        <Text copyable={doesExist(record?.billRate)}>{showDash(record?.billRate)}</Text>
                                                                    </>
                                                                    : <Form.Item
                                                                        label="Bill Rate (USD per Hour)"
                                                                        name="billRate"
                                                                        rules={[
                                                                            {
                                                                                required: validateForm,
                                                                                type: "number"
                                                                            }
                                                                        ]}
                                                                    >
                                                                        <InputNumber min={0} max={200} step={1} precision={2} placeholder="0.00" addonAfter="$" style={{ width: 200 }} />
                                                                    </Form.Item>
                                                                }
                                                            </Col>
                                                        </Row>
                                                    </>
                                                )
                                                    : <>
                                                        <Form.Item
                                                            name="billingType"
                                                            hidden={true}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="billingStartDate"
                                                            hidden={true}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item
                                                            name="billRate"
                                                            hidden={true}
                                                        >
                                                            <Input />
                                                        </Form.Item>
                                                    </>
                                            }
                                        </Form.Item>
                                        <Divider orientation="left">Constraints</Divider>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Work Location Constraints</Title>
                                                        <Text copyable={doesExist(record?.locationConstraint)}>{showDash(record?.locationConstraint)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Work Location Constraints"
                                                        name="locationConstraint"
                                                        tooltip="Where are the associates expected to work from?"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Radio.Group
                                                            optionType="button"
                                                            buttonStyle="solid"
                                                            name="locationConstraint"
                                                            options={(["Remote", "MS Campus", "TCS Campus"]).map(a => a)}
                                                        />
                                                    </Form.Item>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...twoColumnLaytout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Resourcing Constraints</Title>
                                                        <Text copyable={doesExist(record?.resourceConstraint)}>{showDash(record?.resourceConstraint)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Resourcing Constraints"
                                                        name="resourceConstraint"
                                                        //initialValue="None"
                                                        rules={[
                                                            {
                                                                required: false,
                                                                whitespace: true
                                                            }
                                                        ]}
                                                    >
                                                        <Radio.Group
                                                            optionType="button"
                                                            buttonStyle="solid"
                                                            name="resourceConstraint"
                                                            options={(["BA Only", "FTE Only", "Citizen Only", "Trainee Only", "None"]).map(a => a)}
                                                        />
                                                    </Form.Item>
                                                }
                                            </Col>
                                        </Row>
                                    </Panel>
                                    <Panel header={<b>Hiring Team</b>} forceRender key="aboutteam">
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Country</Title>
                                                        <Text copyable={doesExist(record?.country)}>{showDash(record?.country)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        label="Country"
                                                        name="country"
                                                        tooltip="Where is the project executed from?"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                type: "string",
                                                            }
                                                        ]}
                                                    >
                                                        <Select placeholder="Select Country"
                                                            onChange={(_value) => {
                                                                form.setFieldsValue({ 'location': null });
                                                            }}
                                                            showSearch
                                                            {...countryProps}
                                                        >
                                                        </Select>
                                                    </Form.Item>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {isPartialEditable
                                                    ? <>
                                                        <Title level={5}>Location</Title>
                                                        <Text copyable={doesExist(record?.location)}>{showDash(record?.location)}</Text>
                                                    </>
                                                    : <Form.Item
                                                        noStyle
                                                        shouldUpdate={(prevValues, currentValues) => prevValues.country !== currentValues.country}
                                                    >
                                                        {({ getFieldValue }) =>
                                                            <Form.Item
                                                                label="Location"
                                                                name="location"
                                                                tooltip="Where is the project executed from?"
                                                                rules={[
                                                                    {
                                                                        required: validateForm,
                                                                        type: "string"
                                                                    }
                                                                ]}
                                                            >
                                                                <Select placeholder={form.getFieldValue('country') ? "Select Location" : "Select a Country first"} showSearch>
                                                                    {locations?.filter(loc => loc.countryId === getFieldValue("country")).map((m, i) => (
                                                                        <Select.Option value={m.id} key={`lc-${i}`}>{m.name}</Select.Option>
                                                                    ))}
                                                                </Select>
                                                            </Form.Item>
                                                        }
                                                    </Form.Item>
                                                }
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Project Duration (In Months)"
                                                    name="projectDuration"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            type: "integer"
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={1} max={420} precision={0} placeholder="Project Duration" style={{ width: 200 }} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Group"
                                                    name="group"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "string"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Select business group" showSearch>
                                                        {metaConfig?.group.sort().map((m, i) => (
                                                            <Select.Option value={m} key={`grp-${i}`}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Sub Group"
                                                    name="subGroup"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            max: 100
                                                        },
                                                    ]}
                                                >
                                                    <Input placeholder="Provide business sub group (Ex: E360)" />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Customer Interview"
                                                    name="customerInterview"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true
                                                        }
                                                    ]}
                                                >
                                                    <Radio.Group
                                                        optionType="button"
                                                        buttonStyle="solid"
                                                        name="customerInterview"
                                                        options={(["Yes", "No"]).map(a => a)}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Hiring Manager"
                                                    name="hiringManager"
                                                    tooltip="To look up any missing name type the name of the person and then select"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "email"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Search Name or Email" {...userProps} allowClear
                                                        onChange={(e) => { handleSelectChange(form, e, "hiringManager") }} />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Hiring Manager Delegates"
                                                    name="hiringManagerDelegates"
                                                    help="Associate(s) who can act on behalf of Hiring Manager"
                                                    tooltip="To look up any missing name type the name of the person and then select"
                                                    rules={[
                                                        {
                                                            required: false,
                                                            whitespace: true,
                                                            type: "array"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Search Name or Email" mode="multiple" {...userProps} allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="MS Manager"
                                                    name="msManager"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            max: 100
                                                        },
                                                    ]}
                                                >
                                                    <Input placeholder="Name and alias of MS Manager" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Sales Executive (BRM)"
                                                    name="salesExecutive"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "email"
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select Sales Executive (BRM)"
                                                        allowClear
                                                        showSearch
                                                        optionFilterProp="label"
                                                        options={arrayExtensions.sortByName(salesExecutiveProps?.data || []).map((m, i) => ({ value: m.id, label: m.name }))}
                                                        onChange={(e) => { handleSelectChange(form, e, "salesExecutive") }}>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Delivery Head (L1)"
                                                    name="deliveryHead"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "email",
                                                        },
                                                    ]}
                                                >
                                                    <Select
                                                        placeholder="Select Delivery Head (L1)"
                                                        allowClear
                                                        showSearch
                                                        optionFilterProp="label"
                                                        options={arrayExtensions.sortByName(deliveryHeadProps?.data || []).map((m, i) => ({ value: m.id, label: m.name }))}
                                                        onChange={(e) => { handleSelectChange(form, e, "deliveryHead") }} >
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Offshore Delivery Partner (L2)"
                                                    name="deliveryPartnerOffshore"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "email",
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Select Offshore Delivery Partner (L2)" allowClear showSearch
                                                        optionFilterProp="label"
                                                        options={arrayExtensions.sortByName(offshoreDPProps?.data || []).map((m, i) => ({ value: m.id, label: m.name }))}
                                                        onChange={(e) => { handleSelectChange(form, e, "deliveryPartnerOffshore") }}>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Onsite Delivery Partner (L2)"
                                                    name="deliveryPartnerOnsite"
                                                    dependencies={["country"]}
                                                    rules={[
                                                        ({ getFieldValue }) => ({
                                                            required: validateForm && isOnsiteDpRequired(getFieldValue("country")),
                                                            whitespace: true,
                                                            type: "email",
                                                            message: "Onsite Delivery Partner (L2) is required for the selected country.",
                                                        }),
                                                    ]}
                                                >
                                                    <Select placeholder="Select Onsite Delivery Partner (L2)" allowClear showSearch
                                                        optionFilterProp="label"
                                                        options={arrayExtensions.sortByName(onsiteDPProps?.data || []).map((m, i) => ({ value: m.id, label: m.name }))}
                                                        onChange={(e) => { handleSelectChange(form, e, "deliveryPartnerOnsite") }}>
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="TR Interviewers"
                                                    name="trInterviewers"
                                                    tooltip="To look up any missing name type the name of the person and then select"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            whitespace: true,
                                                            type: "array"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Search Name or Email" mode="multiple" {...userProps} allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="MR Interviewers"
                                                    name="mrInterviewers"
                                                    tooltip="To look up any missing name type the name of the person and then select"
                                                    help="C4 and above only"
                                                    rules={[
                                                        {
                                                            required: false,
                                                            whitespace: true,
                                                            type: "array"
                                                        },
                                                    ]}
                                                >
                                                    <Select placeholder="Search Name or Email" mode="multiple" {...userProps} allowClear />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                {requestState === "draft" &&
                                                    <>
                                                        <Form.Item
                                                            label="Requestor"
                                                        >
                                                            <ShowUserCard id={record?.requestor} ></ShowUserCard>
                                                        </Form.Item>
                                                    </>
                                                }
                                            </Col>
                                        </Row>
                                    </Panel>
                                    <Panel header={<b>Skills</b>} forceRender key="skills">
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Skill Category"
                                                    name="skillCategory"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true
                                                        }
                                                    ]}
                                                >
                                                    <Select placeholder="Select skill category from list" showSearch>
                                                        {metaConfig?.skillCategory.sort().map((m, i) => (
                                                            <Select.Option value={m} key={`rr-${i}`}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Required Skills"
                                                    name="requiredSkills"
                                                    tooltip="Select or add skills. Skill should primarily be a combination of alphabets, numbers and supported special characters which include /_-.&#:+() and space character "
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            type: "array"
                                                        },
                                                        {
                                                            pattern: new RegExp(/^[A-Za-z0-9 /_,-.&#:+()]+$/),
                                                            message: "Invalid character found. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                        }
                                                    ]}
                                                >
                                                    <Select mode="tags"
                                                        placeholder="Type a required skill and press enter. You can add as many as you want."
                                                        tokenSeparators={[',', ';']}
                                                    >
                                                        {skillsList?.data.data.map((m, i) => (
                                                            <Select.Option value={m} key={i}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Good to have skills"
                                                    name="goodToHaveSkills"
                                                    tooltip="Select or add skills. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            type: "array"
                                                        },
                                                        {
                                                            pattern: new RegExp(/^[A-Za-z0-9 /_,-.&#:+()]+$/),
                                                            message: "Invalid character found. Skill should be alphanumeric and can include supported special characters. Supported special characters are: /_-.&#:+() and space character"
                                                        }
                                                    ]}
                                                >
                                                    <Select mode="tags"
                                                        placeholder="Type a skill and press enter. You can add as many as you want."
                                                        tokenSeparators={[',', ';']}
                                                    >
                                                        {skillsList?.data.data.map((m, i) => (
                                                            <Select.Option value={m} key={i}>{m}</Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                        <Row gutter={rowGutter}>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Minimum experience required (In Years)"
                                                    name="experienceRequired"
                                                    tooltip="Please enter min experience required as whole number between 0 to 15"
                                                    rules={[
                                                        {
                                                            required: validateForm,
                                                            whitespace: true,
                                                            type: "integer"
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={0} max={15} precision={0} style={{ width: 200 }} />
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                                <Form.Item
                                                    label="Keywords"
                                                    name="keywords"
                                                    rules={[
                                                        {
                                                            required: false,
                                                            type: "array"
                                                        }
                                                    ]}
                                                >
                                                    <Select mode="tags"
                                                        placeholder="Type a keyword and press enter. You can add as many as you want."
                                                        tokenSeparators={[',']}
                                                    >
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col className="gutter-row" {...threeColumnLayout}>
                                            </Col>
                                        </Row>
                                        <Form.Item
                                            label="Responsibilities"
                                            name="responsibilities"
                                            rules={[
                                                {
                                                    required: validateForm,
                                                    whitespace: true,
                                                    max: 10000
                                                },
                                            ]}
                                        >
                                            <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder="Provide detailed description of responsibilities" />
                                        </Form.Item>
                                    </Panel>
                                    {(!requestRules.nonEditableSections.includes("staffing")) &&
                                        <Panel header={<b>Staffing Section</b>} forceRender key="staffing">
                                            <Row gutter={rowGutter}>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Requestor"
                                                        name="requestor"
                                                        tooltip="To look up any missing name type the name of the person and then select"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true,
                                                                type: "email"
                                                            },
                                                        ]}
                                                    >
                                                        <Select placeholder="Search Name or Email" {...userProps} allowClear />
                                                    </Form.Item>
                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Delivery Unit (DU)"
                                                        name="deliveryUnit"
                                                        //initialValue="ADM"
                                                        rules={[
                                                            {
                                                                required: validateForm,
                                                                whitespace: true
                                                            },
                                                        ]}
                                                    >
                                                        <Select placeholder="Select DU" showSearch>
                                                            {metaConfig?.deliveryUnit.sort().map((m, i) => (
                                                                <Select.Option value={m} key={`du-${i}`}>{m}</Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    {isPartialEditable
                                                        ? <>
                                                            <Title level={5}>Request ID</Title>
                                                            {record?.requestID ?
                                                                <Text copyable>{record?.requestID}</Text>
                                                                :
                                                                <Text>Resourcing Team will provide once request is published for fulfillment.</Text>
                                                            }
                                                        </>
                                                        : <Form.Item
                                                            label="Request ID"
                                                            name="requestID"
                                                            validateTrigger={["onBlur", "onChange"]}
                                                            tooltip="Request ID should be a numeric value with 7 or more digits."
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    type: "integer",
                                                                    min: 1000000,
                                                                    message: "Request ID should be a numeric value with 7 or more digits."
                                                                }
                                                            ]}
                                                        >
                                                            <InputNumber min={0} precision={0} placeholder="Please provide Request ID" style={{ width: "100%" }} controls={false} />
                                                        </Form.Item>
                                                    }

                                                </Col>
                                            </Row>
                                            <Row gutter={rowGutter}>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    {isPartialEditable
                                                        ? <>
                                                            <Title level={5}>RGS IDs</Title>
                                                            {arrayExtensions.validateArray(record?.rgsIds ?? []) ? <Text copyable>{arrayJoinString((record?.rgsIds ?? []).map(String), "; ")}</Text> : "None"}
                                                        </>
                                                        : <Form.Item
                                                            name="rgsIds"
                                                            label="RGS IDs"
                                                            tooltip={
                                                                <>
                                                                    RGS ID needs to be a positive number. RGS IDs can be provided in two way:
                                                                    <br />
                                                                    <ol>
                                                                        <li><i>Paste 'SPACE' separated list directly into the provided input box</i></li>
                                                                        <li><i>Type and press Enter/Space bar for providing one RGS ID at a time</i></li>
                                                                    </ol>
                                                                </>
                                                            }
                                                            rules={[
                                                                {
                                                                    required: validateForm,
                                                                    type: "array",
                                                                    len: posCount,
                                                                    message: `Please enter ${posCount} valid RGS ID(s). RGS ID should be greater than 8000000 and less than 30000000`,
                                                                    validator: async (_, value) => {
                                                                        if (validateForm || value) {
                                                                            if (arrayExtensions.validateArray(value) && value.length === posCount && validateRGSIDs(value)) {
                                                                                Promise.resolve();
                                                                            }
                                                                            else {
                                                                                throw new Error("Provide Valid RGS IDs");
                                                                            }
                                                                        }
                                                                    }
                                                                }]}
                                                        >
                                                            <Select mode="tags"
                                                                placeholder="Enter 'SPACE' seperated RGS IDs"
                                                                open={false}
                                                                tokenSeparators={[' ']}
                                                            >
                                                            </Select>
                                                        </Form.Item>
                                                    }
                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        label="Request Received Date"
                                                        name="requestReceivedDate"
                                                    >
                                                        {record &&
                                                            <Tag>{dayjs(record.requestReceivedDate).format("LL")}</Tag>
                                                        }
                                                    </Form.Item>
                                                </Col>
                                                <Col className="gutter-row" {...threeColumnLayout}>
                                                    <Form.Item
                                                        name="positions"
                                                        hidden={true}
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Panel>
                                    }
                                </Collapse>
                                <br />
                                <Form.Item
                                    label="Remarks"
                                    name="remarks"
                                    style={{ marginLeft: 16, marginRight: 16 }}
                                    tooltip="Mention specific candidates identified, RGS auto-generated for replacement requests, Country, City or any other master data additions related to this request here."
                                    rules={[
                                        {
                                            required: false,
                                            whitespace: true,
                                            max: 10000
                                        },
                                    ]}
                                >
                                    <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder="Provide additional details about the request, if any." />
                                </Form.Item>
                            </Form>
                        </Create>
                    </div>
                </Spin>
                :
                <Result
                    status="403"
                    title="Edit operation not allowed."
                    subTitle="Sorry, In current state, you don't have permission to edit this request. Kindly contact Requestor or Resourcing Team for any changes needed."
                    extra={<Button type="primary" onClick={() => list(RESOURCE_PATH.RESOURCINGREQUEST)}>Back to Resourcing Requests</Button>}
                />
    );
};